import React, { useEffect } from "react"

// The Google Maps JS API is typed loosely here: the app only creates maps and markers
export type GoogleMapsApi = any

// Global function the Maps script calls once the API is ready to use
export const googleMapsCallbackName = "onGoogleMapsLoaded"

let loading: Promise<GoogleMapsApi> | undefined

// Set when the key rejects this site (e.g. localhost missing from the key's allowed referrers). Google then
// turns every google.maps method into a no-op, and a new maps.Map(...) throws from inside Google's own code
let authFailed = false

export const googleMapsAuthFailed = () => authFailed

// Google calls the global gm_authFailure once the key check fails
const globals = window as any
const previousAuthFailure = globals.gm_authFailure
globals.gm_authFailure = () => {
    authFailed = true
    previousAuthFailure?.()
}

// With loading=async, google.maps exists before the API is usable, so check for Map itself
const readyGoogleMaps = (): GoogleMapsApi | undefined => {
    const maps = (window as any).google?.maps
    return maps?.Map ? maps : undefined
}

// Adds the Maps <script> once per page; every map on the page waits on the same load
export const loadGoogleMaps = (url: string): Promise<GoogleMapsApi> => {

    const existing = readyGoogleMaps()
    if (existing) {
        return Promise.resolve(existing)
    }

    if (!loading) {
        loading = new Promise((resolve, reject) => {
            const script = document.createElement("script")
            const separator = url.includes("?") ? "&" : "?"
            script.src = `${url}${separator}loading=async&callback=${googleMapsCallbackName}`
            script.async = true

            // Script onload fires before the libraries are in; only the callback means google.maps.Map is ready
            globals[googleMapsCallbackName] = () => {
                delete globals[googleMapsCallbackName]
                resolve(globals.google.maps)
            }
            script.onerror = () => {
                loading = undefined
                delete globals[googleMapsCallbackName]
                script.remove()
                reject(new Error("Google Maps failed to load"))
            }
            document.head.appendChild(script)
        })
    }

    return loading
}

// Returns google.maps once the script from `url` has loaded, undefined until then
export const useGoogleMaps = (url: string) => {

    const [ maps, setMaps ] = React.useState<GoogleMapsApi | undefined>(readyGoogleMaps)

    useEffect(() => {
        let isCurrent = true

        loadGoogleMaps(url)
            .then(m => isCurrent && setMaps(m))
            .catch(() => {})

        return () => { isCurrent = false }
    }, [ url ])

    return maps
}
